import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sym } from '../../ui';
import { useAuthStore, type AdminUser } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import { apiPost } from '../../api/client';
import { UserRole } from '@grosync/common';
import mark from '../../assets/grovision-mark.png';

/* ============================================================================
 * Auth — login / signup (admin account request). Split-pane brand layout.
 * Login is wired to the real backend (apiPost username/password). Signup is a
 * mock approval-request flow (no backend) per the redesign.
 * ========================================================================= */

interface LoginResponse {
  user: AdminUser;
  token: { accessToken: string };
}

type AuthView = 'login' | 'signup';

export default function AuthPage(): React.ReactElement {
  const dark = useThemeStore((s) => s.dark);
  const [view, setView] = useState<AuthView>('login');
  return (
    <div className={dark ? 'md-dark' : 'md-light'}>
      {view === 'login' ? (
        <AdminLogin onSignup={() => setView('signup')} />
      ) : (
        <AdminSignup onBack={() => setView('login')} />
      )}
    </div>
  );
}

/* ---- Brand pane (shared) ---------------------------------------------- */
function AuthBrandPane(): React.ReactElement {
  const features = [
    { icon: 'space_dashboard', t: '실시간 통합 모니터링', s: '전국 농장 상태를 한눈에' },
    { icon: 'eco', t: 'AI 환경 · 생육 분석', s: '영상 분석 기반 생육 측정' },
    { icon: 'shield_person', t: '역할 기반 접근 관리', s: '본사 · 지역 담당자 권한 분리' },
  ];

  // 마우스 인터랙션: 커서 스포트라이트 + 배경 시차(parallax).
  // x,y = 패널 기준 커서 px / nx,ny = 중심 기준 -1..1 / active = 호버 여부.
  const paneRef = useRef<HTMLDivElement>(null);
  const frame = useRef(0);
  const reduce = useRef(
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );
  const [m, setM] = useState({ x: 0, y: 0, nx: 0, ny: 0, active: false });

  const onMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (reduce.current) return;
    const el = paneRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const nx = (x / r.width - 0.5) * 2;
    const ny = (y / r.height - 0.5) * 2;
    if (frame.current) return; // 프레임당 1회만 갱신
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      setM({ x, y, nx, ny, active: true });
    });
  };
  const onLeave = (): void => {
    if (frame.current) {
      cancelAnimationFrame(frame.current);
      frame.current = 0;
    }
    setM((s) => ({ ...s, nx: 0, ny: 0, active: false }));
  };

  return (
    <div
      ref={paneRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{
        flex: '1 1 46%',
        minWidth: 0,
        position: 'relative',
        overflow: 'hidden',
        background:
          'linear-gradient(150deg, var(--gs-primary-40) 0%, var(--gs-primary-30) 42%, var(--gs-secondary-40) 100%)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '56px 56px 48px',
      }}
    >
      {/* 커서를 따라다니는 스포트라이트 */}
      <div
        aria-hidden
        style={{
          position: 'absolute', left: 0, top: 0, width: 460, height: 460, borderRadius: '50%',
          pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.06) 38%, transparent 70%)',
          transform: `translate3d(${m.x - 230}px, ${m.y - 230}px, 0)`,
          opacity: m.active ? 1 : 0,
          transition: 'opacity .35s ease-out, transform .12s ease-out',
          willChange: 'transform, opacity',
        }}
      />
      {/* 배경 원형 — 커서와 같은/반대 방향으로 시차 이동(깊이감) */}
      <div style={{ position: 'absolute', right: -90, top: -60, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', transform: `translate3d(${m.nx * 26}px, ${m.ny * 26}px, 0)`, transition: 'transform .4s ease-out', willChange: 'transform' }} />
      <div style={{ position: 'absolute', left: -70, bottom: -80, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', transform: `translate3d(${m.nx * -34}px, ${m.ny * -34}px, 0)`, transition: 'transform .4s ease-out', willChange: 'transform' }} />

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 60, height: 60, borderRadius: 17, background: '#fff', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 22px -8px rgba(0,0,0,0.4)' }}>
          <img src={mark} alt="GroVision" style={{ width: 44, height: 44, objectFit: 'contain' }} />
        </div>
        <div style={{ lineHeight: 1 }}>
          <div style={{ font: '700 26px/1 var(--gs-font-kr)', letterSpacing: '-0.5px' }}>GroVision</div>
          <div style={{ font: '600 12px/1 var(--gs-font-kr)', marginTop: 7, letterSpacing: 2, opacity: 0.9 }}>환경·생육 모니터링</div>
        </div>
      </div>

      <div style={{ position: 'relative', transform: `translate3d(${m.nx * 10}px, ${m.ny * 8}px, 0)`, transition: 'transform .3s ease-out', willChange: 'transform' }}>
        <div style={{ font: '700 34px/44px var(--gs-font-kr)', letterSpacing: '-0.8px' }}>
          전국 스마트팜을<br />하나의 화면에서
        </div>
        <div style={{ font: '400 15px/24px var(--gs-font-kr)', opacity: 0.9, marginTop: 14, maxWidth: 380 }}>
          농장 · 장비 · 회원을 통합 관리하고, 실시간 환경 데이터와 AI 생육 분석으로 더 나은 의사결정을 지원합니다.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 30 }}>
          {features.map((f) => (
            <div key={f.t} style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0, background: 'rgba(255,255,255,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Sym name={f.icon} size={22} fill={1} style={{ color: '#fff' }} />
              </div>
              <div>
                <div style={{ font: '600 14px/18px var(--gs-font-kr)' }}>{f.t}</div>
                <div style={{ font: '400 12px/16px var(--gs-font-kr)', opacity: 0.82 }}>{f.s}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ position: 'relative', font: '400 12px/16px var(--gs-font-kr)', opacity: 0.8 }}>
        GroVision © 2026 · 농업 환경 모니터링 플랫폼
      </div>
    </div>
  );
}

function AuthScroll({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="gs-scroll-hide" style={{ flex: '1 1 54%', minWidth: 0, overflowY: 'auto', background: 'var(--md-sys-color-surface)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '48px 7%', maxWidth: 560, width: '100%', margin: '0 auto' }}>
        {children}
      </div>
    </div>
  );
}

/* ---- Auth field -------------------------------------------------------- */
interface AuthFieldProps {
  label: string;
  icon?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  valid?: boolean;
  hint?: string;
  hintOk?: boolean;
  trailing?: React.ReactNode;
  onTrailing?: () => void;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoFocus?: boolean;
}
function AuthField({
  label,
  icon,
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  valid = true,
  hint,
  hintOk,
  trailing,
  onTrailing,
  inputMode,
  autoFocus,
}: AuthFieldProps): React.ReactElement {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ font: '600 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', marginBottom: 7 }}>
        {label}
        {required && <span style={{ color: 'var(--gs-error)', marginLeft: 3 }}>*</span>}
      </div>
      <div
        style={{
          height: 52,
          padding: '0 14px',
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: `1.5px solid ${!valid ? 'var(--gs-error)' : 'var(--md-sys-color-outline-variant)'}`,
          background: 'var(--md-sys-color-surface-container-low)',
        }}
      >
        {icon && <Sym name={icon} size={20} style={{ color: 'var(--md-sys-color-on-surface-variant)', flexShrink: 0 }} />}
        <input
          type={type}
          value={value}
          inputMode={inputMode}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', font: '500 15px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}
        />
        {trailing && (
          <button type="button" onClick={onTrailing} style={{ border: 0, background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', flexShrink: 0, padding: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {trailing}
          </button>
        )}
      </div>
      {hint && (
        <div style={{ font: '400 11px/15px var(--gs-font-kr)', margin: '6px 2px 0', color: hintOk ? 'var(--md-sys-color-primary)' : 'var(--gs-error)', display: 'flex', alignItems: 'center', gap: 3 }}>
          <Sym name={hintOk ? 'check_circle' : 'error'} size={13} fill={1} />
          {hint}
        </div>
      )}
    </div>
  );
}

/* ---- Alert dialog (failure popup) -------------------------------------- */
function AuthAlert({
  open,
  message,
  title = '로그인 실패',
  onClose,
}: {
  open: boolean;
  message: string;
  title?: string;
  onClose: () => void;
}): React.ReactElement | null {
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div style={{ width: 360, maxWidth: '92vw', background: 'var(--md-sys-color-surface)', borderRadius: 24, padding: '30px 26px 22px', textAlign: 'center', boxShadow: '0 18px 50px -12px rgba(0,0,0,0.4)', animation: 'modalIn .18s ease-out' }}>
        <div style={{ width: 64, height: 64, borderRadius: 9999, margin: '0 auto', background: 'var(--md-sys-color-error-container)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Sym name="gpp_bad" size={36} fill={1} style={{ color: 'var(--md-sys-color-error)' }} />
        </div>
        <div style={{ font: '700 19px/26px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', marginTop: 18, letterSpacing: '-0.3px' }}>{title}</div>
        <div style={{ font: '400 14px/21px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginTop: 9 }}>{message}</div>
        <button
          type="button"
          autoFocus
          onClick={onClose}
          style={{ width: '100%', height: 50, borderRadius: 14, marginTop: 24, border: 0, background: 'var(--md-sys-color-primary)', color: 'var(--md-sys-color-on-primary)', font: '700 15px/22px var(--gs-font-kr)', cursor: 'pointer' }}
        >
          확인
        </button>
      </div>
    </div>
  );
}

/* 아이디 저장 — username(식별자, 비밀번호 아님)만 보관. 보안 위험 없음. */
const SAVED_USERNAME_KEY = 'grosync-admin-saved-username';

/* ---- Login ------------------------------------------------------------- */
function AdminLogin({ onSignup }: { onSignup: () => void }): React.ReactElement {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [username, setUsername] = useState(() => localStorage.getItem(SAVED_USERNAME_KEY) ?? '');
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saveId, setSaveId] = useState(() => Boolean(localStorage.getItem(SAVED_USERNAME_KEY)));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const canLogin = username.trim().length > 0 && pw.trim().length > 0 && !loading;

  const submit = async () => {
    if (!canLogin) return;
    setError('');
    setLoading(true);
    try {
      const data = await apiPost<LoginResponse>('/api/auth/login', { username, password: pw });
      if (data.user.role === 'VIEWER') {
        setError('관리자 콘솔 접근 권한이 없습니다. 앱을 사용해 주세요.');
        return;
      }
      // 아이디 저장: 인증 성공 시에만 보관/삭제 (토큰은 저장하지 않는다)
      if (saveId) localStorage.setItem(SAVED_USERNAME_KEY, username.trim());
      else localStorage.removeItem(SAVED_USERNAME_KEY);
      login(data.token.accessToken, data.user);
      navigate('/');
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : '';
      setError(
        raw === 'Invalid credentials'
          ? '아이디 또는 비밀번호가 올바르지 않습니다.'
          : raw || '로그인에 실패했습니다',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ height: '100vh', display: 'flex', background: 'var(--md-sys-color-surface)' }}>
      <AuthBrandPane />
      <AuthScroll>
        <div style={{ font: '700 28px/34px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', letterSpacing: '-0.5px' }}>관리자 로그인</div>
        <div style={{ font: '400 14px/20px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginTop: 8, marginBottom: 30 }}>
          GroVision 관리자 콘솔에 오신 것을 환영합니다.
        </div>

        <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <AuthField label="아이디" icon="person" value={username} onChange={setUsername} placeholder="관리자 아이디" autoFocus />
          <AuthField
            label="비밀번호"
            icon="lock"
            value={pw}
            onChange={setPw}
            placeholder="비밀번호 입력"
            type={showPw ? 'text' : 'password'}
            trailing={<Sym name={showPw ? 'visibility_off' : 'visibility'} size={20} />}
            onTrailing={() => setShowPw((s) => !s)}
          />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 2px 26px' }}>
            <label onClick={() => setSaveId((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', font: '500 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
              <span style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, background: saveId ? 'var(--md-sys-color-primary)' : 'transparent', border: saveId ? 0 : '1.5px solid var(--md-sys-color-outline)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                {saveId && <Sym name="check" size={14} style={{ color: 'var(--md-sys-color-on-primary)' }} />}
              </span>
              아이디 저장
            </label>
            <span style={{ font: '500 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-primary)', cursor: 'pointer' }}>비밀번호 찾기</span>
          </div>

          <button
            type="submit"
            disabled={!canLogin}
            style={{
              width: '100%',
              height: 54,
              borderRadius: 14,
              border: 0,
              background: canLogin ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-surface-container-high)',
              color: canLogin ? 'var(--md-sys-color-on-primary)' : 'var(--md-sys-color-on-surface-variant)',
              font: '700 15px/22px var(--gs-font-kr)',
              cursor: canLogin ? 'pointer' : 'default',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <Sym name="login" size={20} />
            {loading ? '로그인 중...' : '로그인'}
          </button>
        </form>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '26px 0' }}>
          <span style={{ flex: 1, height: 1, background: 'var(--md-sys-color-outline-variant)' }} />
          <span style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>또는</span>
          <span style={{ flex: 1, height: 1, background: 'var(--md-sys-color-outline-variant)' }} />
        </div>

        <div style={{ textAlign: 'center', font: '400 14px/20px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
          관리자 계정이 필요하신가요?{' '}
          <span onClick={onSignup} style={{ color: 'var(--md-sys-color-primary)', fontWeight: 700, cursor: 'pointer' }}>회원가입</span>
        </div>
      </AuthScroll>

      <AuthAlert open={!!error} message={error} onClose={() => setError('')} />
    </div>
  );
}

/* ---- 약관 전문 (회원가입 동의 상세) -----------------------------------
 * 표준 템플릿 문구입니다. 실제 서비스 약관/개인정보 처리방침은 법무 검토 후 교체하세요.
 * ----------------------------------------------------------------------- */
const TERMS: Record<'terms' | 'privacy', { title: string; body: string }> = {
  terms: {
    title: '이용약관',
    body: `제1조 (목적)
이 약관은 GroVision(이하 "회사")이 제공하는 농업 환경 모니터링 관리자 콘솔 및 관련 제반 서비스(이하 "서비스")의 이용과 관련하여 회사와 이용자의 권리·의무 및 책임사항을 규정함을 목적으로 합니다.

제2조 (정의)
1. "이용자"란 이 약관에 따라 회사가 승인한 계정으로 서비스를 이용하는 본사 관리자 및 지역 담당자를 말합니다.
2. "계정"이란 이용자 식별과 서비스 이용을 위해 회사가 승인·발급한 아이디 및 권한을 말합니다.

제3조 (약관의 효력 및 변경)
1. 이 약관은 서비스 화면에 게시하거나 기타의 방법으로 공지함으로써 효력이 발생합니다.
2. 회사는 관련 법령을 위배하지 않는 범위에서 약관을 변경할 수 있으며, 변경 시 적용일자 및 사유를 명시하여 사전 공지합니다.

제4조 (이용계약의 체결)
1. 이용계약은 가입 신청자가 약관에 동의하고 가입을 신청한 후 회사(본사 관리자)가 이를 승인함으로써 체결됩니다.
2. 회사는 신청 내용이 허위이거나 승인 요건을 충족하지 못하는 경우 승인을 거부하거나 사후에 이용계약을 해지할 수 있습니다.

제5조 (서비스의 제공 및 변경)
1. 회사는 농장·장비·회원 통합 관리, 실시간 환경 데이터 및 생육 분석 등의 기능을 제공합니다.
2. 회사는 운영상·기술상 필요에 따라 제공 중인 서비스의 전부 또는 일부를 변경할 수 있습니다.

제6조 (이용자의 의무)
1. 이용자는 계정 정보를 선량한 관리자의 주의로 관리해야 하며, 제3자에게 양도·대여할 수 없습니다.
2. 이용자는 서비스를 통해 알게 된 농장·회원 정보를 업무 목적 외로 이용하거나 외부에 유출해서는 안 됩니다.

제7조 (서비스 이용의 제한)
회사는 이용자가 약관 또는 관련 법령을 위반한 경우 사전 통지 후 서비스 이용을 제한하거나 계정을 정지·삭제할 수 있습니다.

제8조 (책임의 제한)
회사는 천재지변, 통신서비스 장애 등 불가항력으로 인하여 서비스를 제공할 수 없는 경우 책임이 면제됩니다.

부칙
이 약관은 게시일로부터 시행합니다.`,
  },
  privacy: {
    title: '개인정보 수집·이용 동의',
    body: `GroVision(이하 "회사")은 관리자 계정 가입 신청 및 운영을 위하여 아래와 같이 개인정보를 수집·이용합니다. 내용을 확인하신 후 동의 여부를 결정해 주세요.

1. 수집 항목
- 필수: 아이디, 이름, 회사 이메일, 휴대폰 번호, 비밀번호
- 선택: 소속·직책
- 자동 수집: 접속 일시, 접속 IP, 서비스 이용 기록

2. 수집·이용 목적
- 관리자 계정 신청 확인 및 본인 식별
- 가입 승인 심사 및 결과 안내
- 서비스 제공·운영 및 부정 이용 방지
- 공지사항 전달 등 운영상 필요한 안내

3. 보유 및 이용 기간
- 회원 탈퇴 또는 계정 삭제 시까지 보유하며, 목적 달성 후 지체 없이 파기합니다.
- 단, 관련 법령에 따라 보존이 필요한 경우 해당 기간 동안 보관합니다.

4. 동의를 거부할 권리 및 불이익
- 귀하는 개인정보 수집·이용 동의를 거부할 권리가 있습니다.
- 다만 필수 항목에 동의하지 않을 경우 관리자 계정 가입 및 서비스 이용이 제한됩니다.`,
  },
};

/* ---- 약관 상세 모달 (스크롤) ------------------------------------------ */
function TermsDialog({
  which,
  onClose,
}: {
  which: 'terms' | 'privacy' | null;
  onClose: () => void;
}): React.ReactElement | null {
  React.useEffect(() => {
    if (!which) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [which, onClose]);

  if (!which) return null;
  const doc = TERMS[which];

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div style={{ width: 560, maxWidth: '94vw', maxHeight: '86vh', background: 'var(--md-sys-color-surface)', borderRadius: 20, display: 'flex', flexDirection: 'column', boxShadow: '0 18px 50px -12px rgba(0,0,0,0.4)', animation: 'modalIn .18s ease-out' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid var(--md-sys-color-outline-variant)' }}>
          <span style={{ font: '700 16px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>{doc.title}</span>
          <button type="button" onClick={onClose} aria-label="닫기" style={{ border: 0, background: 'transparent', cursor: 'pointer', display: 'inline-flex', color: 'var(--md-sys-color-on-surface-variant)', padding: 4 }}>
            <Sym name="close" size={22} />
          </button>
        </div>
        <div className="gs-scroll-hide" style={{ overflowY: 'auto', padding: '20px 22px', font: '400 13px/21px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', whiteSpace: 'pre-line' }}>
          {doc.body}
        </div>
        <div style={{ padding: '14px 22px 18px', borderTop: '1px solid var(--md-sys-color-outline-variant)' }}>
          <button type="button" onClick={onClose} style={{ width: '100%', height: 48, borderRadius: 12, border: 0, background: 'var(--md-sys-color-primary)', color: 'var(--md-sys-color-on-primary)', font: '700 14px/20px var(--gs-font-kr)', cursor: 'pointer' }}>확인</button>
        </div>
      </div>
    </div>
  );
}

/* ---- Signup (account approval request → real backend) ------------------
 * 웹 회원가입은 전원 '지역 담당자(OPERATOR)'로 신청 → 관리자 승인 후 활성화.
 * 일반 사용자는 앱에서 가입(자동 승인). 본사 관리자 계정은 웹 자가입 불가.
 * ----------------------------------------------------------------------- */
interface AgreeState {
  all: boolean;
  terms: boolean;
  privacy: boolean;
}

function AdminSignup({ onBack }: { onBack: () => void }): React.ReactElement {
  const [f, setF] = useState({ username: '', name: '', org: '', email: '', phone: '', pw: '', pw2: '' });
  const [showPw, setShowPw] = useState(false);
  const [agree, setAgree] = useState<AgreeState>({ all: false, terms: false, privacy: false });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [viewTerms, setViewTerms] = useState<'terms' | 'privacy' | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  const usernameOk = /^[a-zA-Z0-9_]{4,30}$/.test(f.username);
  const nameOk = f.name.trim().length > 0;
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email);
  const phoneOk = f.phone.replace(/[^0-9]/g, '').length >= 10;
  const pwOk = f.pw.length >= 8;
  const pw2Ok = f.pw2.length > 0 && f.pw === f.pw2;
  const reqAgree = agree.terms && agree.privacy;
  const canSubmit = usernameOk && nameOk && emailOk && phoneOk && pwOk && pw2Ok && reqAgree && !submitting;
  const roleLabel = '지역 담당자'; // 웹 회원가입은 전원 지역 담당자(OPERATOR)

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      await apiPost('/api/auth/request-access', {
        username: f.username,
        password: f.pw,
        name: f.name.trim(),
        phone: f.phone,
        email: f.email || undefined,
        requestedRole: UserRole.OPERATOR,
        org: f.org || undefined,
      });
      setDone(true);
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : '가입 신청에 실패했습니다');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAll = () => {
    const n = !agree.all;
    setAgree({ all: n, terms: n, privacy: n });
  };
  const toggleOne = (k: 'terms' | 'privacy') =>
    setAgree((a) => {
      const n = { ...a, [k]: !a[k] };
      n.all = n.terms && n.privacy;
      return n;
    });

  if (done) {
    const rows: [string, string][] = [
      ['아이디', f.username],
      ['이름', f.name],
      ['소속·직책', f.org || '미입력'],
      ['권한', roleLabel],
      ['이메일', f.email],
      ['연락처', f.phone],
    ];
    return (
      <div style={{ height: '100vh', display: 'flex', background: 'var(--md-sys-color-surface)' }}>
        <AuthBrandPane />
        <AuthScroll>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <div style={{ width: 104, height: 104, borderRadius: 9999, background: 'var(--md-sys-color-primary-container)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sym name="how_to_reg" size={60} fill={1} style={{ color: 'var(--md-sys-color-primary)' }} />
            </div>
            <div style={{ font: '700 26px/32px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', marginTop: 24, letterSpacing: '-0.5px' }}>가입 신청이 접수되었습니다</div>
            <div style={{ font: '400 14px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginTop: 10, maxWidth: 380 }}>
              <b style={{ color: 'var(--md-sys-color-on-surface)' }}>{f.name}</b> 님의 <b style={{ color: 'var(--md-sys-color-on-surface)' }}>{roleLabel}</b> 계정 신청이 접수되었습니다. 본사 승인 후 <span className="gs-num">{f.email}</span>로 안내 메일을 보내드립니다. (영업일 1일 이내)
            </div>
            <div style={{ width: '100%', marginTop: 26, padding: '16px 18px', borderRadius: 16, textAlign: 'left', background: 'var(--md-sys-color-surface-container-low)', border: '1px solid var(--md-sys-color-outline-variant)' }}>
              {rows.map(([k, v], i) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i === rows.length - 1 ? 0 : '1px solid var(--md-sys-color-outline-variant)' }}>
                  <span style={{ font: '500 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{k}</span>
                  <span className="gs-num" style={{ font: '600 14px/20px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>{v}</span>
                </div>
              ))}
            </div>
            <button onClick={onBack} style={{ width: '100%', height: 54, borderRadius: 14, marginTop: 24, border: 0, background: 'var(--md-sys-color-primary)', color: 'var(--md-sys-color-on-primary)', font: '700 15px/22px var(--gs-font-kr)', cursor: 'pointer' }}>로그인으로 돌아가기</button>
          </div>
        </AuthScroll>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', background: 'var(--md-sys-color-surface)' }}>
      <AuthBrandPane />
      <AuthScroll>
        <button onClick={onBack} style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 5, marginBottom: 18, border: 0, background: 'transparent', cursor: 'pointer', font: '600 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
          <Sym name="arrow_back" size={18} />로그인으로
        </button>

        <div style={{ font: '700 28px/34px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', letterSpacing: '-0.5px' }}>담당자 회원가입</div>
        <div style={{ font: '400 14px/20px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginTop: 8, marginBottom: 20 }}>
          지역 담당자 계정을 신청하면 관리자 승인 후 콘솔을 이용할 수 있습니다.
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 22, padding: '13px 15px', borderRadius: 14, border: '1.5px solid var(--md-sys-color-outline-variant)', background: 'var(--md-sys-color-surface-container-lowest)' }}>
          <Sym name="shield_person" size={24} fill={1} style={{ color: 'var(--md-sys-color-primary)', flexShrink: 0 }} />
          <div>
            <div style={{ font: '700 14px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>지역 담당자</div>
            <div style={{ font: '400 11px/15px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>담당 지역 농장 관리 · 일반 사용자는 앱에서 가입해 주세요</div>
          </div>
        </div>

        <AuthField label="아이디" icon="account_circle" required value={f.username} onChange={(v) => set('username', v)} placeholder="로그인 아이디 (영문/숫자/밑줄 4자 이상)" valid={!f.username || usernameOk} hint={f.username && !usernameOk ? '영문·숫자·밑줄 4~30자로 입력해주세요' : ''} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <AuthField label="이름" icon="person" required value={f.name} onChange={(v) => set('name', v)} placeholder="실명 입력" />
          <AuthField label="소속 · 직책" icon="badge" value={f.org} onChange={(v) => set('org', v)} placeholder="예: 운영팀 매니저" />
        </div>
        <AuthField label="회사 이메일" icon="mail" required value={f.email} onChange={(v) => set('email', v)} placeholder="name@company.com" inputMode="email" valid={!f.email || emailOk} hint={f.email && !emailOk ? '올바른 이메일 형식이 아닙니다' : ''} />
        <AuthField label="휴대폰 번호" icon="call" required value={f.phone} onChange={(v) => set('phone', v)} placeholder="010-0000-0000" inputMode="tel" valid={!f.phone || phoneOk} hint={f.phone && !phoneOk ? '10자리 이상 숫자를 입력해주세요' : ''} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <AuthField label="비밀번호" icon="lock" required type={showPw ? 'text' : 'password'} value={f.pw} onChange={(v) => set('pw', v)} placeholder="8자 이상" valid={!f.pw || pwOk} hint={f.pw && !pwOk ? '8자 이상 입력해주세요' : ''} trailing={<Sym name={showPw ? 'visibility_off' : 'visibility'} size={20} />} onTrailing={() => setShowPw((s) => !s)} />
          <AuthField label="비밀번호 확인" icon="lock_reset" required type={showPw ? 'text' : 'password'} value={f.pw2} onChange={(v) => set('pw2', v)} placeholder="비밀번호 재입력" valid={!f.pw2 || pw2Ok} hint={f.pw2 ? (pw2Ok ? '비밀번호가 일치합니다' : '비밀번호가 일치하지 않습니다') : ''} hintOk={pw2Ok} />
        </div>

        <div style={{ marginTop: 8, borderRadius: 14, border: '1px solid var(--md-sys-color-outline-variant)', background: 'var(--md-sys-color-surface-container-low)', padding: '4px 6px' }}>
          <AgreeLine checked={agree.all} onClick={toggleAll} bold label="약관 전체 동의" />
          <div style={{ height: 1, background: 'var(--md-sys-color-outline-variant)', margin: '2px 14px' }} />
          <AgreeLine checked={agree.terms} onClick={() => toggleOne('terms')} required label="이용약관 동의" link onDetail={() => setViewTerms('terms')} />
          <AgreeLine checked={agree.privacy} onClick={() => toggleOne('privacy')} required label="개인정보 수집·이용 동의" link onDetail={() => setViewTerms('privacy')} />
        </div>

        {submitError && (
          <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 12, background: 'var(--md-sys-color-error-container)', color: 'var(--md-sys-color-on-error-container)', font: '500 13px/18px var(--gs-font-kr)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sym name="error" size={18} fill={1} />
            {submitError}
          </div>
        )}

        <button onClick={() => void submit()} disabled={!canSubmit} style={{ width: '100%', height: 54, borderRadius: 14, marginTop: 24, border: 0, background: canSubmit ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-surface-container-high)', color: canSubmit ? 'var(--md-sys-color-on-primary)' : 'var(--md-sys-color-on-surface-variant)', font: '700 15px/22px var(--gs-font-kr)', cursor: canSubmit ? 'pointer' : 'default' }}>{submitting ? '신청 중...' : '가입 신청'}</button>

        <div style={{ textAlign: 'center', marginTop: 16, font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
          이미 계정이 있으신가요?{' '}
          <span onClick={onBack} style={{ color: 'var(--md-sys-color-primary)', fontWeight: 700, cursor: 'pointer' }}>로그인</span>
        </div>

        <TermsDialog which={viewTerms} onClose={() => setViewTerms(null)} />
      </AuthScroll>
    </div>
  );
}

function AgreeLine({
  checked,
  onClick,
  label,
  required,
  bold,
  link,
  onDetail,
}: {
  checked: boolean;
  onClick: () => void;
  label: string;
  required?: boolean;
  bold?: boolean;
  link?: boolean;
  onDetail?: () => void;
}): React.ReactElement {
  return (
    <div className="gs-row" onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 12px', cursor: 'pointer', borderRadius: 10 }}>
      <span style={{ width: 22, height: 22, borderRadius: 9999, flexShrink: 0, background: checked ? 'var(--md-sys-color-primary)' : 'transparent', border: checked ? 0 : '1.5px solid var(--md-sys-color-outline)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Sym name="check" size={15} style={{ color: checked ? 'var(--md-sys-color-on-primary)' : 'var(--md-sys-color-outline)' }} />
      </span>
      <span style={{ flex: 1, font: `${bold ? 600 : 400} ${bold ? 14 : 13}px/18px var(--gs-font-kr)`, color: 'var(--md-sys-color-on-surface)' }}>
        {required && <span style={{ color: 'var(--md-sys-color-primary)', marginRight: 3 }}>(필수)</span>}
        {label}
      </span>
      {link && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDetail?.(); }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexShrink: 0, border: 0, background: 'transparent', cursor: 'pointer', padding: '2px 2px 2px 6px', font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', textDecoration: 'underline', textUnderlineOffset: 3 }}
        >
          보기
          <Sym name="chevron_right" size={16} style={{ textDecoration: 'none' }} />
        </button>
      )}
    </div>
  );
}
